/**
 * Who an audit event says acted, as a reader would name them.
 *
 * @module components/shared/audit-actor
 */

import type { AuditEvent } from "@/server/types";

function metadataString(event: AuditEvent, key: string): string | undefined {
  const value = event.metadata?.[key];
  return typeof value === "string" && value.trim() ? value : undefined;
}

/**
 * The actor's name, followed by the operator when the session was impersonated.
 * A platform admin is not in the tenant's directory, so the name recorded with
 * the event stands in for them.
 *
 * @example
 * ```ts
 * auditActorLabel(event, names); // "Sabrina, impersonated by Sam Cogan"
 * ```
 */
export function auditActorLabel(event: AuditEvent, users: ReadonlyMap<string, string>): string {
  const platformAdmin =
    event.metadata?.actorRole === "platform-admin" ? "Platform administrator" : undefined;
  const actor =
    users.get(event.actor) ?? metadataString(event, "actorName") ?? platformAdmin ?? event.actor;
  const operatorId = metadataString(event, "impersonatedBy");
  if (!operatorId) {
    return actor;
  }
  const operator =
    metadataString(event, "impersonatedByName") ??
    users.get(operatorId) ??
    "a platform administrator";
  return `${actor}, impersonated by ${operator}`;
}
